import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  FaFacebook, 
  FaTwitter, 
  FaInstagram, 
  FaLinkedin, 
  FaMapMarkerAlt, 
  FaPhone, 
  FaEnvelope 
} from 'react-icons/fa';

const EditFooterModal = ({ isOpen, onClose, initialData }) => {
  // Default data structure
  const DEFAULT_DATA = {
    logoUrl: "https://jgalfalah.com/backend/media/uploaded_images/uploaded_images/warm-wellness-center-logo-cs-2025-cs-removebg-preview.png",
    aboutText: "Warm Wellness Center's Commitment to Advanced Health Care Solutions.",
    quickLinks: [
      { text: "About Us", url: "#" },
      { text: "Services", url: "#" },
      { text: "Our Doctors", url: "#" },
      { text: "Health Packages", url: "#" },
      { text: "Career", url: "#" },
      { text: "Test Prices", url: "#" },
      { text: "Online Report", url: "#" }
    ],
    serviceLinks: [
      { text: "Doctor Consultation", url: "#" },
      { text: "Appointment Booking", url: "#" },
      { text: "Pharmacy", url: "#" },
      { text: "Media Center", url: "#" },
      { text: "Terms and Conditions", url: "#" },
      { text: "Privacy Policy", url: "#" },
      { text: "Contact Us", url: "#" }
    ],
    socialLinks: [
      { platform: "facebook", url: "#" },
      { platform: "twitter", url: "#" },
      { platform: "instagram", url: "#" },
      { platform: "linkedin", url: "#" }
    ],
    contactInfo: {
      address: "167, Tropical Cantt View, 1st floor, 4 Matikata Rd, Dhaka 1206",
      phone: "+880 9606 990123",
      email: "info@jghealthcare.com"
    },
    copyrightText: `© ${new Date().getFullYear()} Blockchain Technologies. All Rights Reserved.`,
    colors: {
      primary: '#0760ad',
      secondary: '#098a48',
      lightBg: 'rgba(255, 255, 255, 0.1)'
    }
  };

  // State management
  const [footerData, setFooterData] = useState(DEFAULT_DATA);
  const [loadingStates, setLoadingStates] = useState({
    isLoading: false,
    isUploadingLogo: false,
    uploadProgress: 0
  });

  const apiUrl = import.meta.env.VITE_API_URL;

  // Fetch data when modal opens
  useEffect(() => {
    const fetchData = async () => {
      if (isOpen) {
        setLoadingStates(prev => ({ ...prev, isLoading: true }));
        try {
          const response = await fetch(`${apiUrl}/layout/footer/`);
          if (response.ok) {
            const data = await response.json();
            
            // Transform legacy data format (array of strings) to new format (array of objects)
            const transformLinks = (links) => {
              if (!links) return [];
              return links.map(link => 
                typeof link === 'string' ? { text: link, url: '#' } : link
              );
            };
            
            const transformedData = {
              ...DEFAULT_DATA,
              ...data,
              quickLinks: transformLinks(data.quickLinks),
              serviceLinks: transformLinks(data.serviceLinks)
            };
            
            setFooterData(transformedData);
          }
        } catch (error) {
          console.error("Fetch error:", error);
        } finally {
          setLoadingStates(prev => ({ ...prev, isLoading: false }));
        }
      }
    };
    fetchData();
  }, [isOpen]);

  // Handle text changes
  const handleTextChange = (e) => {
    const { name, value } = e.target;
    setFooterData(prev => ({ ...prev, [name]: value }));
  };

  // Handle contact info changes
  const handleContactChange = (field, value) => {
    setFooterData(prev => ({
      ...prev,
      contactInfo: {
        ...prev.contactInfo,
        [field]: value
      }
    }));
  };

  // Handle link text or URL changes
  const handleLinkChange = (type, index, field, value) => {
    const updatedLinks = [...footerData[type]];
    updatedLinks[index] = { ...updatedLinks[index], [field]: value };
    setFooterData(prev => ({ ...prev, [type]: updatedLinks }));
  };

  // Handle social link changes
  const handleSocialLinkChange = (index, field, value) => {
    const updatedSocial = [...footerData.socialLinks];
    updatedSocial[index] = { ...updatedSocial[index], [field]: value };
    setFooterData(prev => ({ ...prev, socialLinks: updatedSocial }));
  };

  // Handle logo upload
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const form = new FormData();
    form.append('image', file);
    form.append('category', 'footer_logo');

    try {
      setLoadingStates(prev => ({ 
        ...prev, 
        isUploadingLogo: true,
        uploadProgress: 0 
      }));

      const response = await axios.post(`${apiUrl}/images/`, form, {
        onUploadProgress: (progress) => {
          const percent = Math.round((progress.loaded * 100) / progress.total);
          setLoadingStates(prev => ({ ...prev, uploadProgress: percent }));
        }
      });

      const imageUrl = response.data.image?.url || response.data.image;
      if (imageUrl) {
        setFooterData(prev => ({ ...prev, logoUrl: imageUrl }));
      }
    } catch (error) {
      console.error("Upload error:", error);
      alert("Logo upload failed. Please try again.");
    } finally {
      setLoadingStates(prev => ({ 
        ...prev, 
        isUploadingLogo: false
      }));
    }
  };

  // Add/remove links
  const addLink = (type) => {
    setFooterData(prev => ({
      ...prev,
      [type]: [...prev[type], { text: "New Link", url: "#" }]
    }));
  };

  const removeLink = (type, index) => {
    const updatedLinks = [...footerData[type]];
    updatedLinks.splice(index, 1);
    setFooterData(prev => ({ ...prev, [type]: updatedLinks }));
  };

  // Add/remove social links
  const addSocialLink = () => {
    setFooterData(prev => ({
      ...prev,
      socialLinks: [...prev.socialLinks, { platform: "facebook", url: "#" }]
    }));
  };

  const removeSocialLink = (index) => {
    const updatedSocial = [...footerData.socialLinks];
    updatedSocial.splice(index, 1);
    setFooterData(prev => ({ ...prev, socialLinks: updatedSocial }));
  };

  // Submit form
  const submitForm = async () => {
    try {
      setLoadingStates(prev => ({ ...prev, isLoading: true }));
      const response = await fetch(`${apiUrl}/layout/footer/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(footerData)
      });

      if (response.ok) {
        onClose();
      } else {
        throw new Error("Update failed");
      }
    } catch (error) {
      console.error("Submit error:", error);
      alert("Failed to save changes");
    } finally {
      setLoadingStates(prev => ({ ...prev, isLoading: false }));
    }
  };

  // Loading state
  if (loadingStates.isLoading) {
    return (
      <dialog className="modal modal-open">
        <div className="modal-box">
          <div className="flex flex-col items-center justify-center p-8">
            <span className="loading loading-spinner loading-lg text-primary"></span>
            <p className="mt-4 text-gray-600">Loading footer data...</p>
          </div>
        </div>
      </dialog>
    );
  }

  return (
    <dialog className={`modal ${isOpen ? 'modal-open' : ''}`}>
      <div className="modal-box w-11/12 max-w-6xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold">Edit Footer</h3>
          <button onClick={onClose} className="btn btn-circle btn-sm">
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto pr-2 space-y-6">
          {/* Logo and About Section */}
          <div className="card bg-base-100 p-4">
            <h4 className="font-medium mb-4">Logo & About</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Logo</span>
                </label>
                <div className="flex items-center gap-2">
                  {footerData.logoUrl && (
                    <div className="avatar">
                      <div className="w-16 rounded">
                        <img src={footerData.logoUrl} alt="Logo preview" />
                      </div>
                    </div>
                  )}
                  <input 
                    type="file" 
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="file-input file-input-bordered w-full"
                    disabled={loadingStates.isUploadingLogo}
                  />
                  {loadingStates.isUploadingLogo && (
                    <progress 
                      className="progress progress-primary w-20" 
                      value={loadingStates.uploadProgress} 
                      max="100"
                    />
                  )}
                </div>
              </div>
              
              <div className="form-control">
                <label className="label">
                  <span className="label-text">About Text</span>
                </label>
                <textarea
                  name="aboutText"
                  value={footerData.aboutText}
                  onChange={handleTextChange}
                  className="textarea textarea-bordered h-24"
                />
              </div>
            </div>
          </div>

          {/* Quick Links Section */}
          <div className="card bg-base-100 p-4">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-medium">Quick Links ({footerData.quickLinks.length})</h4>
              <button 
                onClick={() => addLink('quickLinks')}
                className="btn btn-primary btn-sm"
              >
                + Add Link
              </button>
            </div>
            
            <div className="space-y-4">
              {footerData.quickLinks.map((link, index) => (
                <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <div className="form-control">
                    <input
                      type="text"
                      value={link.text}
                      onChange={(e) => handleLinkChange('quickLinks', index, 'text', e.target.value)}
                      className="input input-bordered"
                      placeholder="Link Text"
                    />
                  </div>
                  <div className="form-control md:col-span-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={link.url}
                        onChange={(e) => handleLinkChange('quickLinks', index, 'url', e.target.value)}
                        className="input input-bordered flex-1"
                        placeholder="URL (e.g., /about or https://example.com)"
                      />
                      <button 
                        onClick={() => removeLink('quickLinks', index)}
                        className="btn btn-error btn-sm btn-circle"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Service Links Section */}
          <div className="card bg-base-100 p-4">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-medium">Service Links ({footerData.serviceLinks.length})</h4>
              <button 
                onClick={() => addLink('serviceLinks')}
                className="btn btn-primary btn-sm"
              >
                + Add Link
              </button>
            </div>
            
            <div className="space-y-4">
              {footerData.serviceLinks.map((link, index) => (
                <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <div className="form-control">
                    <input
                      type="text"
                      value={link.text}
                      onChange={(e) => handleLinkChange('serviceLinks', index, 'text', e.target.value)}
                      className="input input-bordered"
                      placeholder="Link Text"
                    />
                  </div>
                  <div className="form-control md:col-span-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={link.url}
                        onChange={(e) => handleLinkChange('serviceLinks', index, 'url', e.target.value)}
                        className="input input-bordered flex-1"
                        placeholder="URL (e.g., /services or https://example.com)"
                      />
                      <button 
                        onClick={() => removeLink('serviceLinks', index)}
                        className="btn btn-error btn-sm btn-circle"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Social Links Section */}
          <div className="card bg-base-100 p-4">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-medium">Social Links ({footerData.socialLinks.length})</h4>
              <button 
                onClick={addSocialLink}
                className="btn btn-primary btn-sm"
              >
                + Add Social
              </button>
            </div>
            
            <div className="space-y-4">
              {footerData.socialLinks.map((social, index) => (
                <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text">Platform</span>
                    </label>
                    <select
                      value={social.platform}
                      onChange={(e) => handleSocialLinkChange(index, 'platform', e.target.value)}
                      className="select select-bordered"
                    >
                      <option value="facebook">Facebook</option>
                      <option value="twitter">Twitter</option>
                      <option value="instagram">Instagram</option>
                      <option value="linkedin">LinkedIn</option>
                      <option value="youtube">YouTube</option>
                    </select>
                  </div>
                  
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text">URL</span>
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={social.url}
                        onChange={(e) => handleSocialLinkChange(index, 'url', e.target.value)}
                        className="input input-bordered flex-1"
                        placeholder="https://"
                      />
                      <button 
                        onClick={() => removeSocialLink(index)}
                        className="btn btn-error btn-sm btn-circle"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Contact Information */}
          <div className="card bg-base-100 p-4">
            <h4 className="font-medium mb-4">Contact Information</h4>
            
            <div className="space-y-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Address</span>
                </label>
                <textarea
                  value={footerData.contactInfo.address}
                  onChange={(e) => handleContactChange('address', e.target.value)}
                  className="textarea textarea-bordered"
                  rows="3"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Phone</span>
                  </label>
                  <input
                    type="text"
                    value={footerData.contactInfo.phone}
                    onChange={(e) => handleContactChange('phone', e.target.value)}
                    className="input input-bordered"
                  />
                </div>
                
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Email</span>
                  </label>
                  <input
                    type="email"
                    value={footerData.contactInfo.email}
                    onChange={(e) => handleContactChange('email', e.target.value)}
                    className="input input-bordered"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Copyright Text */}
          <div className="form-control">
            <label className="label">
              <span className="label-text">Copyright Text</span>
            </label>
            <input
              type="text"
              name="copyrightText"
              value={footerData.copyrightText}
              onChange={handleTextChange}
              className="input input-bordered"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="modal-action mt-6">
          <button onClick={onClose} className="btn btn-ghost">
            Cancel
          </button>
          <button 
            onClick={submitForm} 
            className="btn btn-primary"
            disabled={loadingStates.isLoading}
          >
            {loadingStates.isLoading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </dialog>
  );
};

export default EditFooterModal;